"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import styled, { useTheme } from "styled-components";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useAuth } from "react-oidc-context";
import Image from "next/image";
import DePuySynthesLogo from "@/assets/logos/DePuySynthes.svg";
import ChevronIcon from "@/assets/icons/Chevron.svg";
import ReturnIcon from "@/assets/icons/ReturnIcon.svg";
import AddIcon from "@/assets/icons/AddIcon.svg";
import {
  adminNavItems,
  organizationLinks,
  settingsLinks,
} from "../../constants/adminItems";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { Icon } from "../Icon/Icon";
import Button from "../Button/Button";
import { LocaleDropdown } from "../Dropdown/LocaleDropdown";
import { SUPPORTED_LOCALES } from "@/lib/constants/locale";
import { useTranslations } from "next-intl";
import WarningModal from "../Modal/WarningModal";
import { UnsavedChangesContent } from "../Modal/UnsavedChangesContent";

//#region styled
const NavContainer = styled.div`
  width: 100vw;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const NavigationContainer = styled.div<{ $bckgColor: string }>`
  background-color: ${(props) => props.$bckgColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 62px;
  padding: 0 25px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 15px 0;
`;

const NavLinks = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
`;

const ClickableContainer = styled.div`
  padding: 15px 25px;
  cursor: pointer;
`;

const PrimaryItem = styled.div`
  display: flex;
  gap: 10px;
  color: ${({ theme }) => theme.main};
  border-bottom: 2px solid transparent;
  align-items: center;
  cursor: pointer;
  padding-bottom: 4px;
  position: relative;

  .nav-p {
    font-size: 1.25rem;
  }

  &.selected {
    border-color: ${({ theme }) => theme.main};
  }

  .settings-hovered {
    transform: rotate(180deg);
  }
`;

const SecondaryItem = styled.div`
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  color: ${({ theme }) => theme.black};

  &.selected {
    color: ${({ theme }) => theme.main};
  }
`;

const SettingsHover = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  gap: 10px;
  border-radius: 5px;
  background: ${(props) => props.theme.white};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  z-index: 100000;
  position: absolute;
  right: 0;
  top: 36px;
  min-width: 100px;
`;

const SecondaryFunctions = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding-right: 30px;
`;
//#endregion

export default function AdminHeadNav() {
  const params = useParams();
  const { locale, orgId, userId } = params;
  const router = useRouter();
  const pathname = usePathname();
  const auth = useAuth();
  const theme = useTheme();
  const signOutRedirect = useBoundStore((state) => state.signOutRedirect);
  const selectedLocale = useBoundStore((state) => state.selectedLocale);
  const setSelectedLocale = useBoundStore((state) => state.setSelectedLocale);
  const setModalType = useBoundStore((state) => state.setModalType);
  const setSelectedAdminOrg = useBoundStore(
    (state) => state.setSelectedAdminOrg
  );
  const setSelectedOrgId = useBoundStore((state) => state.setSelectedOrgId);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);
  const isEditCareplan = useBoundStore((state) => state.isEditCareplan);
  const setIsEditCareplan = useBoundStore((state) => state.setIsEditCareplan);
  const isCreateOrgCareplan = useBoundStore(
    (state) => state.isCreateOrgCareplan
  );
  const setSelectedCarePlanId = useBoundStore(
    (state) => state.setSelectedCarePlanId
  );
  const orgProfileMode = useBoundStore((state) => state.orgProfileMode);
  const userProfileMode = useBoundStore((state) => state.userProfileMode);
  const adminModalOpen = useBoundStore((state) => state.adminModalOpen);
  const adminModalType = useBoundStore((state) => state.adminModalType);
  const setAdminModalOpen = useBoundStore((state) => state.setAdminModalOpen);
  const setAdminModalType = useBoundStore((state) => state.setAdminModalType);
  const setPendingNavigation = useBoundStore(
    (state) => state.setPendingNavigation
  );
  const handleCloseAdminModal = useBoundStore(
    (state) => state.handleCloseAdminModal
  );
  const [settingsHovered, setSettingsHovered] = useState(false);
  const t = useTranslations();
  const tAdmin = useTranslations("adminDashboard");

  useEffect(() => {
    if (typeof locale === "string" && locale) {
      setSelectedLocale(locale);
    }
  }, [locale]);

  useEffect(() => {
    if (selectedLocale) {
      const segments = pathname.split("/").filter(Boolean);
      segments.shift();
      const newPath = "/" + segments.join("/");
      router.replace(`/${selectedLocale}${newPath}`);
    }
  }, [selectedLocale]);

  const getSelectedItem = (name: string) => {
    return pathname.includes(`/${name}`);
  };

  const handlePageSelect = (route: string) => {
    const loc = typeof locale === "string" ? locale : "en-US";
    router.push(`/${loc}${route}`);
  };

  const returnToDashboard = () => {
    const loc = typeof locale === "string" ? locale : "en-US";
    const destination = isOrgContext
      ? `/${loc}/admin/organizations`
      : `/${loc}/admin/users`;

    if (isUserContext && userProfileMode === "edit") {
      setPendingNavigation(destination);
      setAdminModalType("unsaved-changes-nav");
      setAdminModalOpen(true);
      return;
    }

    if (
      isOrgContext &&
      (orgProfileMode === "edit" ||
        orgProfileMode === "create" ||
        isEditCareplan ||
        isCreateOrgCareplan)
    ) {
      setPendingNavigation(destination);
      setAdminModalType("unsaved-changes-nav");
      setAdminModalOpen(true);
      return;
    }

    if (isOrgContext) {
      handlePageSelect("/admin/organizations");
      setSelectedAdminOrg(null);
      setSelectedOrgId("");
    } else {
      handlePageSelect("/admin/users");
    }
  };

  const createGlobalCareplan = () => {
    setModalType("create-global-careplan");
    setModalOpen(true);
    setIsEditCareplan(false);
    setSelectedCarePlanId(null);
  };

  const createOrgCareplan = () => {
    setModalType("create-org-careplan");
    setModalOpen(true);
  };

  const isOrgContext = pathname.includes("/admin/organizations/") && !!orgId;
  const isUserContext = pathname.includes("/admin/users/") && !!userId;
  const showSecondaryBar = isOrgContext || isUserContext;

  const handlePrimaryNavClick = (e: React.MouseEvent, href: string) => {
    const inUnsavedState =
      (isOrgContext &&
        (orgProfileMode === "edit" || orgProfileMode === "create")) ||
      (isUserContext && userProfileMode === "edit") ||
      isEditCareplan ||
      isCreateOrgCareplan;

    if (inUnsavedState) {
      e.preventDefault();
      setPendingNavigation(href);
      setAdminModalType("unsaved-changes-nav");
      setAdminModalOpen(true);
    } else {
      setSelectedAdminOrg(null);
      setSelectedOrgId("");
    }
  };
  const globalCarePlansBar = pathname.includes("/admin/careplans");

  const loc = typeof locale === "string" ? locale : "en-US";

  return (
    <NavContainer>
      <NavigationContainer $bckgColor={theme.white}>
        <LogoContainer>
          <Image
            src={DePuySynthesLogo.src}
            alt="DePuy Synthes"
            width={264}
            height={40}
            style={{ height: "auto", maxHeight: "40px", width: "auto" }}
          />
        </LogoContainer>

        {auth.isAuthenticated && (
          <NavLinks>
            {adminNavItems.map((item) => {
              const renderPrimaryItem = () => (
                <Link
                  href={item.name !== "settings" ? `/${loc}${item.route}` : ""}
                  key={item.name}
                  passHref
                  onClick={
                    item.name !== "settings"
                      ? (e) => handlePrimaryNavClick(e, `/${loc}${item.route}`)
                      : undefined
                  }
                >
                  <ClickableContainer>
                    <PrimaryItem
                      className={`${getSelectedItem(item.name) ? "selected" : ""}`}
                    >
                      <p className="nav-p johnson-display">{t(item.text)}</p>
                      {item.name === "settings" && (
                        <Icon
                          url={ChevronIcon.src}
                          color={theme.main}
                          height="18px"
                          width="18px"
                          customClass={`${settingsHovered ? "settings-hovered" : ""}`}
                        />
                      )}
                    </PrimaryItem>
                  </ClickableContainer>
                </Link>
              );

              if (item.name !== "settings") {
                return renderPrimaryItem();
              }

              return (
                <div
                  key={item.name}
                  style={{ position: "relative" }}
                  onMouseEnter={() => setSettingsHovered(true)}
                  onMouseLeave={() => setSettingsHovered(false)}
                >
                  {renderPrimaryItem()}
                  {settingsHovered && (
                    <SettingsHover>
                      {settingsLinks.map((subItem) => {
                        if (subItem.name === "logout") {
                          return (
                            <PrimaryItem
                              key={subItem.name}
                              onClick={() => signOutRedirect()}
                              onMouseEnter={() => setSettingsHovered(true)}
                            >
                              <p
                                className="nav-p johnson-display"
                                style={{ color: theme.black }}
                              >
                                {t(subItem.text)}
                              </p>
                            </PrimaryItem>
                          );
                        }
                        return (
                          <Link
                            href={`/${loc}${subItem.route}`}
                            key={subItem.name}
                            passHref
                            onClick={(e) =>
                              handlePrimaryNavClick(
                                e,
                                `/${loc}${subItem.route}`
                              )
                            }
                          >
                            <PrimaryItem
                              onMouseEnter={() => setSettingsHovered(true)}
                              className={`${getSelectedItem(subItem.name) ? "selected" : ""}`}
                            >
                              <p className="nav-p johnson-display">
                                {t(subItem.text)}
                              </p>
                            </PrimaryItem>
                          </Link>
                        );
                      })}
                    </SettingsHover>
                  )}
                </div>
              );
            })}
            <LocaleDropdown
              options={SUPPORTED_LOCALES}
              selectedOption={selectedLocale}
              onSelect={(e) => {
                const value = (e.target as HTMLLIElement).getAttribute(
                  "data-value"
                );
                setSelectedLocale(value!);
              }}
              color={theme.main}
              backgroundColor={theme.white}
              styles={{ marginRight: 20 }}
            />
          </NavLinks>
        )}
      </NavigationContainer>

      {globalCarePlansBar && (
        <>
          <NavigationContainer
            $bckgColor={theme.lightGrey}
            style={{ minHeight: "44px", justifyContent: "flex-end" }}
          >
            <SecondaryFunctions>
              <Button
                label={tAdmin("careplans.createNewCarePlan")}
                icon={AddIcon.src}
                name="create-global-care-plan"
                theme="primary"
                onPress={createGlobalCareplan}
                isDisabled={isEditCareplan}
              />
            </SecondaryFunctions>
          </NavigationContainer>
        </>
      )}

      {showSecondaryBar && (
        <NavigationContainer
          $bckgColor={theme.lightGrey}
          style={{ minHeight: "44px" }}
        >
          <NavLinks>
            {isOrgContext &&
              organizationLinks.map((item) => {
                const href = `/${loc}/admin/organizations/${orgId}/${item.route}`;
                const isDisabled = orgProfileMode === "create";

                const handleOrgLinkClick = (e: React.MouseEvent) => {
                  if (isDisabled) {
                    e.preventDefault();
                    return;
                  }
                  if (
                    orgProfileMode === "edit" ||
                    isEditCareplan ||
                    isCreateOrgCareplan
                  ) {
                    e.preventDefault();
                    setPendingNavigation(href);
                    setAdminModalType("unsaved-changes-nav");
                    setAdminModalOpen(true);
                  }
                };

                return (
                  <Link
                    href={href}
                    key={item.name}
                    passHref
                    onClick={handleOrgLinkClick}
                    style={
                      isDisabled
                        ? { opacity: 0.5, pointerEvents: "none" }
                        : undefined
                    }
                  >
                    <ClickableContainer>
                      <SecondaryItem
                        className={`${getSelectedItem(item.name) ? "selected" : ""}`}
                      >
                        <p>{t(item.text)}</p>
                      </SecondaryItem>
                    </ClickableContainer>
                  </Link>
                );
              })}
          </NavLinks>
          <SecondaryFunctions>
            {pathname.includes("org-careplans") && (
              <Button
                label={t("adminOrganizationsPage.createOrgCareplan")}
                name="create-org-care-plan"
                theme="secondary"
                icon={AddIcon.src}
                onPress={createOrgCareplan}
                isDisabled={isEditCareplan}
              />
            )}
            <Button
              label={t("componentText.returnDashboard")}
              icon={ReturnIcon.src}
              name="return-to-dashboard"
              theme="primary"
              onPress={returnToDashboard}
            />
          </SecondaryFunctions>
        </NavigationContainer>
      )}
      {adminModalOpen && adminModalType === "unsaved-changes-nav" && (
        <WarningModal
          modalWidth="40%"
          toggleModal={handleCloseAdminModal}
          content={<UnsavedChangesContent />}
        />
      )}
    </NavContainer>
  );
}
